import type { QuestionnaireResponseItemAnswer } from '@medplum/fhirtypes';

function formatCoding({ display, code }: { display?: string; code?: string }): string | null {
  // Prefer the human-readable display; the terminology code is noise in a read-only view.
  return display ?? code ?? null;
}

function formatQuantity({ value, unit }: { value?: number; unit?: string }): string | null {
  if (value === undefined) return null;
  return unit ? `${value} ${unit}` : value.toString();
}

/**
 * Format a `QuestionnaireResponse.item.answer` value for display, walking the FHIR
 * `value*` polymorphic shape and returning the most user-readable string available.
 *
 * Returns `null` if the answer carries no recognizable value field (callers can
 * render their own "No answer" placeholder or omit the row).
 */
export function formatQuestionnaireAnswer(answer: QuestionnaireResponseItemAnswer): string | null {
  // Plain primitives — return as-is or stringified.
  if (answer.valueString !== undefined) return answer.valueString;
  if (answer.valueBoolean !== undefined) return answer.valueBoolean ? 'Yes' : 'No';
  if (answer.valueInteger !== undefined) return answer.valueInteger.toString();
  if (answer.valueDecimal !== undefined) return answer.valueDecimal.toString();

  // Date/time/uri — strings already, just need to detect presence.
  const stringValue = answer.valueDate ?? answer.valueDateTime ?? answer.valueTime ?? answer.valueUri;
  if (stringValue !== undefined) return stringValue;

  // Compound types — delegate to formatters that pick the best display field.
  if (answer.valueCoding) return formatCoding(answer.valueCoding);
  if (answer.valueQuantity) return formatQuantity(answer.valueQuantity);
  if (answer.valueReference) return answer.valueReference.display ?? answer.valueReference.reference ?? null;
  if (answer.valueAttachment) return answer.valueAttachment.title ?? answer.valueAttachment.url ?? null;

  return null;
}
